import type { Params } from '@angular/router';
import type { CockpitFilterConfig, CockpitFilterDateFormat, CockpitFilterSource } from '../models/cockpit-filter-config.model';

const hasKey = (params: Params, key: string): boolean => Object.prototype.hasOwnProperty.call(params, key);

/** Préserve les valeurs de destination autorisées, puis applique les transferts disponibles. */
export function mapCockpitFilters(config: CockpitFilterConfig, sourceParams: Params, targetParams: Params): Params {
  const result: Params = {};
  const allowedKeys = new Set([...config.defaultFilters, ...config.keepFilters].map(({ key }) => key));

  for (const key of allowedKeys) {
    if (hasKey(targetParams, key)) result[key] = targetParams[key];
  }

  for (const { key, sourceKeys = [] } of config.keepFilters) {
    for (const source of [key, ...sourceKeys]) {
      const resolved = resolveFilterSource(source, sourceParams);
      if (resolved !== undefined) {
        result[key] = resolved.value;
        break;
      }
    }
  }

  return result;
}

function resolveFilterSource(source: CockpitFilterSource, params: Params): { value: unknown } | undefined {
  if (typeof source === 'string') {
    // La présence de la clé reste prioritaire, même pour une valeur explicitement vidée.
    return hasKey(params, source) ? { value: params[source] } : undefined;
  }

  if (!source.separator) return undefined;

  if (source.type === 'join') {
    const values = source.keys.map((key) => readSingleValue(params, key));
    if (values.some((value) => value === undefined)) return undefined;
    if (values.every((value) => value === '')) return { value: '' };
    if (values.some((value) => value === '')) return undefined;

    const formatted = values.map((value) => formatFilterDate(value, source.dateFormat));
    if (formatted.some((value) => value === undefined)) return undefined;
    return { value: formatted.join(source.separator) };
  }

  const value = readSingleValue(params, source.key);
  if (value === undefined || !Number.isInteger(source.index) || source.index < 0) return undefined;
  if (value === '') return { value: '' };

  const parts = value.split(source.separator);
  if (parts.length < 2 || source.index >= parts.length || parts.some((part) => part === '')) return undefined;
  const formatted = formatFilterDate(parts[source.index], source.dateFormat);
  return formatted === undefined ? undefined : { value: formatted };
}

function formatFilterDate(value: string | undefined, format?: CockpitFilterDateFormat): string | undefined {
  if (format === undefined || value === undefined) return value;

  // Lire la date telle qu'écrite évite les décalages de jour liés au fuseau horaire.
  const text = value.trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s,])/);
  const french = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:$|[\s,])/);
  if (!iso && !french) return undefined;

  const year = Number(iso ? iso[1] : french![3]);
  const month = Number(iso ? iso[2] : french![2]);
  const day = Number(iso ? iso[3] : french![1]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysPerMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysPerMonth[month - 1]) return undefined;

  const yyyy = String(year).padStart(4, '0');
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return format === 'YYYY-MM-DD' ? `${yyyy}-${mm}-${dd}` : `${dd}/${mm}/${yyyy}`;
}

/** Un query param répété est ambigu pour une conversion ; un tableau d'une valeur est accepté. */
function readSingleValue(params: Params, key: string): string | undefined {
  if (!hasKey(params, key)) return undefined;
  const value: unknown = params[key];
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.length === 1 && typeof value[0] === 'string') return value[0];
  return undefined;
}
